// Secure PIN charset (excludes confusing characters)
// Excluded: 0, O (zero/oh), 1, I, L (one/eye/el), 5, S (five/ess), 2, Z (two/zee)
const PIN_CHARSET = 'ABCDEFGHJKMNPQRTUVWXY346789';

// Normalize PIN: uppercase, remove invalid chars
export function normalizePIN(pin: string): string {
  return pin.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Validate PIN contains only allowed characters
export function isValidPIN(pin: string): boolean {
  const normalized = normalizePIN(pin);
  if (normalized.length !== 6) return false;
  return [...normalized].every(char => PIN_CHARSET.includes(char));
}

// Generate random 6-digit PIN
export function generatePIN(): string {
  const array = new Uint8Array(6);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => PIN_CHARSET[byte % PIN_CHARSET.length]).join('');
}

// Generate random salt
export function generateSalt(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Layer 1: PBKDF2 slow stretching (~100ms)
async function stretchSecret(uuid: string, pin: string, salt: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const normalizedPIN = normalizePIN(pin);
  const password = `${uuid}:${normalizedPIN}`;
  const saltWithPrefix = `qc-v1:${salt}`;

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  return crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: encoder.encode(saltWithPrefix),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    256
  );
}

// Layer 2: HKDF for auth token
async function deriveAuthToken(stretched: ArrayBuffer, uuid: string): Promise<string> {
  const encoder = new TextEncoder();

  const key = await crypto.subtle.importKey('raw', stretched, 'HKDF', false, ['deriveBits']);

  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      salt: encoder.encode(uuid),
      info: encoder.encode('auth'),
      hash: 'SHA-256',
    },
    key,
    256
  );

  return Array.from(new Uint8Array(bits), (b) => b.toString(16).padStart(2, '0')).join('');
}

// Layer 3: HKDF for encryption key
async function deriveCryptoKey(stretched: ArrayBuffer, uuid: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();

  const key = await crypto.subtle.importKey('raw', stretched, 'HKDF', false, ['deriveKey']);

  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      salt: encoder.encode(uuid),
      info: encoder.encode('encrypt'),
      hash: 'SHA-256',
    },
    key,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// Three-layer key derivation
export async function deriveKeys(
  uuid: string,
  pin: string,
  salt: string
): Promise<{ authToken: string; cryptoKey: CryptoKey; authHash: string }> {
  // Layer 1: Slow stretch
  const stretched = await stretchSecret(uuid, pin, salt);

  // Layer 2: Auth token
  const authToken = await deriveAuthToken(stretched, uuid);

  // Layer 3: Encryption key
  const cryptoKey = await deriveCryptoKey(stretched, uuid);

  // Compute auth hash for server storage
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(authToken));
  const authHash = Array.from(new Uint8Array(hashBuffer), (b) => b.toString(16).padStart(2, '0')).join('');

  return { authToken, cryptoKey, authHash };
}

// Encrypt text
export async function encrypt(cryptoKey: CryptoKey, plaintext: string): Promise<string> {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    encoder.encode(plaintext)
  );

  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);

  // Use chunking to avoid stack overflow on iOS Safari
  const chunkSize = 65536;
  let result = '';
  for (let i = 0; i < combined.length; i += chunkSize) {
    result += String.fromCharCode(...combined.subarray(i, i + chunkSize));
  }
  return btoa(result);
}

// Decrypt text
export async function decrypt(cryptoKey: CryptoKey, payload: string): Promise<string> {
  const decoder = new TextDecoder();
  const combined = new Uint8Array(
    atob(payload)
      .split('')
      .map((c) => c.charCodeAt(0))
  );

  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    ciphertext
  );

  return decoder.decode(plaintext);
}

// Encrypt file (returns base64 string)
export async function encryptFile(cryptoKey: CryptoKey, file: ArrayBuffer): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    file
  );

  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);

  // Use chunking to avoid stack overflow on iOS Safari
  const chunkSize = 65536;
  let result = '';
  for (let i = 0; i < combined.length; i += chunkSize) {
    result += String.fromCharCode(...combined.subarray(i, i + chunkSize));
  }
  return btoa(result);
}

// Decrypt file
export async function decryptFile(cryptoKey: CryptoKey, payload: string): Promise<ArrayBuffer> {
  const combined = new Uint8Array(
    atob(payload)
      .split('')
      .map((c) => c.charCodeAt(0))
  );

  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  return crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, ciphertext);
}

// Convert ArrayBuffer to base64
// Uses chunking to avoid stack overflow on iOS Safari
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 65536; // Process in 64KB chunks to avoid stack overflow
  let result = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    result += String.fromCharCode(...chunk);
  }

  return btoa(result);
}

// Convert base64 to ArrayBuffer
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Re-export secure storage functions
export {
  saveCredentials,
  loadCredentials,
  clearCredentials,
  clearAllCredentials,
  hasCredentials,
} from './localStorage';

// Re-export machine salt functions
export {
  getMachineSalt,
  isMachineSaltAvailable,
} from './machineSalt';
